import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useUserOrg } from '../lib/org';
import { getSite, createPermit, type Site } from '../lib/db';
import { uploadPermit } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { PERMIT_TEMPLATES } from '../data/permitTemplates';
import { generatePermitPdf } from '../lib/pdf';
import { LoadingIndicator } from '../components/LoadingIndicator';

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

function plusHours(hours: number): string {
  const d = new Date();
  d.setHours(d.getHours() + hours);
  // datetime-local needs YYYY-MM-DDTHH:MM
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function PermitFillForm() {
  const { siteId, templateId } = useParams<{ siteId: string; templateId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const permitIdRef = useRef<string>(crypto.randomUUID());
  const sigRef = useRef<SignaturePadHandle>(null);

  const template = PERMIT_TEMPLATES.find((t) => t.id === templateId);

  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [issuedToName, setIssuedToName] = useState('');
  const [issuedToCompany, setIssuedToCompany] = useState('');
  const [issuedByName, setIssuedByName] = useState('');
  const [expiresAt, setExpiresAt] = useState(plusHours(8));
  const [signOffChecks, setSignOffChecks] = useState<boolean[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!siteId) return;
    setLoading(true);
    (async () => {
      try {
        setSite(await getSite(siteId));
        if (template) {
          setSignOffChecks(template.signOffChecks.map(() => false));
          setExpiresAt(plusHours(template.defaultDurationHours));
        }
        const { data } = await supabase.auth.getUser();
        const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
        if (name) setIssuedByName(name);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [siteId, templateId, template]);

  if (loading) return <AppShell title="Loading…"><LoadingIndicator /></AppShell>;
  if (!template) return <AppShell title="Permit"><div className="banner banner-error">Permit template not found.</div></AppShell>;
  if (!site) return <AppShell title="Permit"><div className="banner banner-error">Site not found.</div></AppShell>;

  const allChecksTicked = signOffChecks.every(Boolean);

  function failWith(msg: string) {
    setError(msg);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!template || !site) return;
    if (!org) { failWith('Organisation not loaded yet. Wait a moment and try again.'); return; }
    if (!issuedToName.trim()) { failWith('Issued to: name is required.'); return; }
    if (!issuedByName.trim()) { failWith('Issued by: name is required.'); return; }
    if (!allChecksTicked) { failWith('All sign-off checks must be ticked before issuing.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const permitId = permitIdRef.current;
      const issuedAtIso = new Date().toISOString();

      let signatureDataUrl: string | null = null;
      if (sigRef.current && !sigRef.current.isEmpty()) {
        signatureDataUrl = await blobToDataUrl(await sigRef.current.toBlob());
      }

      const dateLabel = new Date(issuedAtIso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
      const expiryLabel = new Date(expiresAt).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });

      const blob = await generatePermitPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: template.title,
        permitType: template.type,
        siteName: site.name,
        issuedToName: issuedToName.trim(),
        issuedToCompany: issuedToCompany.trim() || null,
        issuedByName: issuedByName.trim(),
        issuedAt: dateLabel,
        expiresAt: expiryLabel,
        description: template.description,
        prerequisites: template.prerequisites,
        controls: template.controls,
        signOffChecks: template.signOffChecks.map((label, i) => ({ label, checked: signOffChecks[i] ?? false })),
        fireWatchMinutes: template.fireWatchMinutes ?? null,
        signatureDataUrl,
        notes: notes.trim() || null,
      });

      const fileName = `${template.id}-${site.name.replace(/\s+/g, '-')}-${dateLabel.replace(/[^\w]/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const path = await uploadPermit(org.org_id, permitId, file);

      await createPermit({
        id: permitId,
        orgId: org.org_id,
        siteId: site.id,
        templateId: template.id,
        title: template.title,
        permitType: template.type,
        issuedToName: issuedToName.trim(),
        issuedToCompany: issuedToCompany.trim() || null,
        expiresAt: new Date(expiresAt).toISOString(),
        fireWatchMinutes: template.fireWatchMinutes ?? null,
        notes: notes.trim() || null,
        filePath: path,
        fileName,
      });

      // Land on the freshly-issued permit's view page with ?share=1 so the
      // ShareModal auto-opens — lets the user email the permit straight to
      // the contractor doing the work.
      nav(`/permits/${permitId}?share=1`);
    } catch (e) {
      console.error('[PermitFillForm] issue failed:', e);
      failWith((e instanceof Error ? e.message : String(e)) + ' — check browser console for details.');
      setSubmitting(false);
    }
  }

  return (
    <AppShell title={template.title} back={`/sites/${site.id}/permits`}>
      {/* Intro card — title is already in the AppShell header, no need
          to repeat it here. Description + site/fire-watch metadata still
          earn their place. */}
      <div className="card" style={{ marginBottom: 12 }}>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>{template.description}</p>
        <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
          Site: <strong>{site.name}</strong>
          {template.fireWatchMinutes ? ` · fire watch ${template.fireWatchMinutes} min` : ''}
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      <form onSubmit={onSubmit}>
        <h3 className="section-title">Permit details</h3>
        <div className="field needs-input">
          <label htmlFor="p-to-name">Issued to (name) *</label>
          <input id="p-to-name" type="text" value={issuedToName} onChange={(e) => setIssuedToName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="p-to-co">Issued to (company)</label>
          <input id="p-to-co" type="text" value={issuedToCompany} onChange={(e) => setIssuedToCompany(e.target.value)} />
        </div>
        <div className="field needs-input">
          <label htmlFor="p-expiry">Expires *</label>
          <input
            id="p-expiry" type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            required
          />
        </div>
        <div className="field needs-input">
          <label htmlFor="p-by">Issued by *</label>
          <input id="p-by" type="text" value={issuedByName} onChange={(e) => setIssuedByName(e.target.value)} required />
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Prerequisites</h3>
        <ul className="muted" style={{ paddingLeft: 16, fontSize: 13 }}>
          {template.prerequisites.map((p, i) => (<li key={i}>{p}</li>))}
        </ul>

        <h3 className="section-title" style={{ marginTop: 22 }}>Controls during work</h3>
        <ul className="muted" style={{ paddingLeft: 16, fontSize: 13 }}>
          {template.controls.map((c, i) => (<li key={i}>{c}</li>))}
        </ul>

        <h3 className="section-title" style={{ marginTop: 22 }}>Sign-off — confirm before issuing</h3>
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
          You are personally responsible for these confirmations.
        </p>
        <div className="check-list">
          {template.signOffChecks.map((label, i) => (
            <label key={i} className="check-row">
              <input
                type="checkbox"
                checked={signOffChecks[i] ?? false}
                onChange={(e) => {
                  const next = signOffChecks.slice();
                  next[i] = e.target.checked;
                  setSignOffChecks(next);
                }}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="p-notes">Notes (optional)</label>
          <textarea id="p-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <h3 className="section-title" style={{ marginTop: 22 }}>Issue signature</h3>
        <SignaturePad ref={sigRef} label="Sign as issuer" />

        {!allChecksTicked && (
          <p className="muted" style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--danger)' }}>
            ⚠ Tick every sign-off check before the Issue button activates.
          </p>
        )}

        <div className="btn-stack">
          <button type="submit" className="btn btn-primary" disabled={submitting || !allChecksTicked}>
            {submitting ? 'Generating…' : 'Issue permit'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => nav(`/sites/${site.id}/permits`)}>
            Cancel
          </button>
        </div>
      </form>
    </AppShell>
  );
}
